import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Clock, DollarSign, ArrowRight, Monitor, MapPin } from 'lucide-react';
import { useLocalCurrency } from '@/hooks/useLocalCurrency';




interface FeaturedService {
  id: string;
  service_name: string;
  description: string | null;
  price: number;
  currency: string;
  duration_minutes: number;
  formats_available: string[];
  practitioner_first_name: string | null;
  practitioner_last_name: string | null;
  practitioner_slug: string | null;
  practitioner_avatar: string | null;
}

const FeaturedServices: React.FC = () => {
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  
  const isZh = i18n.language?.startsWith('zh');

  const { data: services = [], isLoading, error } = useQuery({
    queryKey: ['featured-services'],
    queryFn: async () => {
      // Try featured_services table first
      const { data: featured } = await supabase
        .from('featured_services')
        .select(`
          service_id,
          sort_order,
          service:services(
            id, service_name, description, price, currency, duration_minutes, formats_available,
            practitioners!inner(first_name, last_name, slug, avatar_url)
          )
        `)
        .eq('is_active', true)
        .order('sort_order', { ascending: true })
        .limit(6);

      const featuredServices = (featured || [])
        .filter((f: any) => f.service)
        .map((f: any) => {
          const s = f.service;
          return {
            id: s.id,
            service_name: s.service_name,
            description: s.description,
            price: s.price,
            currency: s.currency,
            duration_minutes: s.duration_minutes,
            formats_available: s.formats_available || [],
            practitioner_first_name: s.practitioners?.first_name,
            practitioner_last_name: s.practitioners?.last_name,
            practitioner_slug: s.practitioners?.slug,
            practitioner_avatar: s.practitioners?.avatar_url,
          };
        }) as FeaturedService[];

      if (featuredServices.length > 0) return featuredServices;

      // Fallback: fetch latest active services
      const { data, error } = await supabase
        .from('services')
        .select(`
          id, service_name, description, price, currency, duration_minutes, formats_available,
          practitioners!inner(first_name, last_name, slug, avatar_url, is_verified, listing_enabled)
        `)
        .eq('is_active', true)
        .gt('price', 0)
        .order('created_at', { ascending: false })
        .limit(6);

      if (error) throw error;

      return (data || [])
        .filter((s: any) => !/test/i.test(s.service_name))
        .map((s: any) => ({
          id: s.id,
          service_name: s.service_name,
          description: s.description,
          price: s.price,
          currency: s.currency,
          duration_minutes: s.duration_minutes,
          formats_available: s.formats_available || [],
          practitioner_first_name: s.practitioners?.first_name,
          practitioner_last_name: s.practitioners?.last_name,
          practitioner_slug: s.practitioners?.slug,
          practitioner_avatar: s.practitioners?.avatar_url,
        })) as FeaturedService[];
    },
    staleTime: 5 * 60 * 1000,
    retry: 2,
  });

  const { formatLocal } = useLocalCurrency();
  const formatPrice = (price: number, currency: string) => {
    return formatLocal(price, currency || 'USD');
  };


  const truncateDescription = (desc: string | null, maxLen = 80) => {
    if (!desc) return '';
    return desc.length > maxLen ? desc.slice(0, maxLen) + '...' : desc;
  };

  if (isLoading) {
    return (
      <section className="py-12 bg-brand-100/60">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-brand text-warm-800 mb-8 text-center">
            {isZh ? '精选服务' : 'Featured Services'}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-40 bg-warm-100 rounded-xl animate-pulse" />
            ))}
          </div>
        </div>
      </section>
    );
  }

  if (error || services.length === 0) return null;

  return (
    <section className="py-12 bg-brand-100/60">
      <div className="container mx-auto px-4">
        <div className="text-center mb-8">
          <h2 className="text-3xl md:text-4xl font-brand text-warm-800">
            {isZh ? '精选服务' : 'Featured Services'}
          </h2>
          <p className="text-sm text-muted-foreground mt-2">
            {isZh ? '探索我们疗愈师提供的专业服务' : 'Explore professional services offered by our practitioners'}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {services.map((service, index) => (
            <Card
              key={service.id}
              className="overflow-hidden hover:shadow-md transition-all duration-300 bg-white border border-warm-200 cursor-pointer group"
              onClick={() => {
                if (service.practitioner_slug) {
                  navigate(`/book-an-appointment/${service.practitioner_slug}`);
                }
              }}
            >
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-2 mb-2">
                  <h3 className="text-base font-semibold text-foreground line-clamp-1 group-hover:text-brand-600 transition-colors">
                    {service.service_name}
                  </h3>
                  <span className="text-sm font-bold text-brand-600 whitespace-nowrap">
                    {formatPrice(service.price, service.currency)}
                  </span>
                </div>

                {service.description && (
                  <p className="text-xs text-muted-foreground mb-3 line-clamp-2">
                    {truncateDescription(service.description)}
                  </p>
                )}

                <div className="flex items-center gap-3 text-xs text-warm-600 mb-3">
                  <span className="flex items-center gap-1">
                    <Clock className="h-3 w-3" />
                    {service.duration_minutes} min
                  </span>
                  {service.formats_available.map((f) => (
                    <span key={f} className="flex items-center gap-1">
                      {f === 'virtual' ? <Monitor className="h-3 w-3" /> : <MapPin className="h-3 w-3" />}
                      {f === 'virtual' ? (isZh ? '线上' : 'Virtual') : (isZh ? '线下' : 'In-Person')}
                    </span>
                  ))}
                </div>

                <div className="flex items-center justify-between">
                  <p className="text-xs text-muted-foreground">
                    {isZh ? '提供者：' : 'By '}
                    <span className="font-medium text-foreground">
                      {service.practitioner_first_name} {service.practitioner_last_name}
                    </span>
                  </p>
                  <ArrowRight className="h-3.5 w-3.5 text-muted-foreground group-hover:text-brand-500 transition-colors" />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        <div className="flex justify-center mt-8">
          <Button
            onClick={() => navigate('/booking-choice')}
            className="bg-brand-100 text-brand-700 hover:bg-brand-200 border border-brand-300 px-8 py-3 font-medium shadow-none"
          >
            {isZh ? '浏览所有服务' : 'Browse All Services'}
            <ArrowRight className="h-4 w-4 ml-2" />
          </Button>
        </div>
      </div>
    </section>
  );
};

export default FeaturedServices;
